import {
  defaultLightTheme,
  mergeTheme,
  type CourierInboxTheme,
} from '@trycourier/courier-react';

const PRIMARY = 'hsl(var(--primary))';
const BORDER = 'hsl(var(--border))';
const FOREGROUND = 'hsl(var(--foreground))';
const MUTED_FG = 'hsl(var(--muted-foreground))';
const MUTED_BG = 'hsl(var(--muted))';
const BACKGROUND = 'hsl(var(--background))';
const ACCENT_BG = 'hsl(var(--accent))';
const HEADING_FONT = 'var(--font-heading, "Bebas Neue", "Helvetica Neue", sans-serif)';
const BODY_FONT = 'var(--font-sans, "Inter", "Helvetica Neue", sans-serif)';

const tab = (color: string, weight: string) => ({
  font: { family: HEADING_FONT, weight, size: '12px', color },
});

const override: CourierInboxTheme = {
  inbox: {
    header: {
      backgroundColor: BACKGROUND,
      border: `1px solid ${BORDER}`,
      shadow: `inset 0 -1px 0 ${BORDER}`,
      feeds: {
        button: {
          font: {
            family: HEADING_FONT,
            weight: '700',
            size: '16px',
            color: FOREGROUND,
          },
          unreadCountIndicator: {
            font: { family: BODY_FONT, weight: '600', size: '11px', color: BACKGROUND },
            backgroundColor: PRIMARY,
            borderRadius: '999px',
            padding: '2px 8px',
          },
        },
      },
      tabs: {
        borderRadius: '0px',
        default: {
          ...tab(MUTED_FG, '600'),
          backgroundColor: 'transparent',
          hoverBackgroundColor: MUTED_BG,
          indicatorColor: 'transparent',
          indicatorHeight: '2px',
          unreadIndicator: {
            font: {
              family: BODY_FONT,
              weight: '700',
              size: '11px',
              color: 'hsl(var(--primary-foreground))',
            },
            backgroundColor: PRIMARY,
            borderRadius: '999px',
            padding: '2px 7px',
          },
        },
        selected: {
          ...tab(PRIMARY, '700'),
          backgroundColor: 'transparent',
          hoverBackgroundColor: MUTED_BG,
          indicatorColor: PRIMARY,
          indicatorHeight: '2px',
          unreadIndicator: {
            font: {
              family: BODY_FONT,
              weight: '700',
              size: '11px',
              color: 'hsl(var(--primary-foreground))',
            },
            backgroundColor: PRIMARY,
            borderRadius: '999px',
            padding: '2px 7px',
          },
        },
      },
      actions: {
        button: {
          icon: { color: MUTED_FG },
          backgroundColor: 'transparent',
          hoverBackgroundColor: MUTED_BG,
        },
        markAllRead: { icon: { color: FOREGROUND } },
        archiveAll: { icon: { color: FOREGROUND } },
        archiveRead: { icon: { color: FOREGROUND } },
        menu: {
          backgroundColor: BACKGROUND,
          border: `1px solid ${BORDER}`,
          borderRadius: '8px',
          shadow: '0 8px 24px rgba(0,0,0,0.08)',
          list: {
            font: { family: BODY_FONT, weight: '500', size: '14px', color: FOREGROUND },
            selectedIcon: { color: FOREGROUND },
            hoverBackgroundColor: ACCENT_BG,
            activeBackgroundColor: MUTED_BG,
            divider: `1px solid ${BORDER}`,
          },
        },
      },
    },
    list: {
      backgroundColor: BACKGROUND,
      item: {
        backgroundColor: BACKGROUND,
        hoverBackgroundColor: ACCENT_BG,
        unreadIndicatorColor: PRIMARY,
        divider: `1px solid ${BORDER}`,
        title: { family: BODY_FONT, weight: '600', size: '14px', color: FOREGROUND },
        subtitle: { family: BODY_FONT, weight: '400', size: '13px', color: MUTED_FG },
        time: { family: BODY_FONT, weight: '400', size: '12px', color: MUTED_FG },
      },
    },
    empty: {
      title: {
        font: { family: HEADING_FONT, weight: '700', size: '14px', color: FOREGROUND },
        text: 'No notifications',
      },
      button: {
        font: {
          family: HEADING_FONT,
          weight: '700',
          size: '12px',
          color: 'hsl(var(--primary-foreground))',
        },
        text: 'Refresh',
        border: 'none',
        borderRadius: '4px',
        backgroundColor: PRIMARY,
        hoverBackgroundColor: 'hsl(var(--primary) / 0.9)',
        activeBackgroundColor: 'hsl(var(--primary) / 0.85)',
      },
    },
    error: {
      title: {
        font: { family: HEADING_FONT, weight: '700', size: '14px', color: 'hsl(var(--destructive))' },
      },
      button: {
        font: {
          family: HEADING_FONT,
          weight: '700',
          size: '12px',
          color: 'hsl(var(--primary-foreground))',
        },
        border: 'none',
        borderRadius: '4px',
        backgroundColor: PRIMARY,
        hoverBackgroundColor: 'hsl(var(--primary) / 0.9)',
        activeBackgroundColor: 'hsl(var(--primary) / 0.85)',
      },
    },
    loading: {
      animation: { barColor: PRIMARY, barHeight: '3px', barBorderRadius: '999px' },
      divider: `1px solid ${BORDER}`,
    },
  },
};

export const courierInboxTheme = mergeTheme(defaultLightTheme, override);
